import {
  foundationConsoleSessionV1Schema,
  customerTypeChangeApplyResponseV1Schema,
  customerTypeChangePreviewResponseV1Schema,
  customerToCashReportV1Schema,
  customerStatementV1Schema,
  dispatchBoardActionRequestV1Schema,
  dispatchBoardActionResponseV1Schema,
  dispatchBoardReadV1Schema,
  dispatchDayOfChangeRequestV1Schema,
  dispatchDayOfChangeResponseV1Schema,
  dispatchOptimizationJobV1Schema,
  dispatchOptimizationStartRequestV1Schema,
  dispatchOptimizationStartResponseV1Schema,
  supportCaseBoardV1Schema,
  operationsDashboardV1Schema,
  foundationMutationResponseV1Schema,
  financeConsoleCommandDraftV1Schema,
  financeReferenceDataRequestV1Schema,
  financeReferenceDataV1Schema,
  commandResultV1Schema,
  financialReportV1Schema,
  foundationProjectionReadV1Schema,
  internalCustomerCreateResponseV1Schema,
  internalCustomerDuplicatePreviewResponseV1Schema,
  sandboxCustomerToCashRequestV1Schema,
  sandboxCustomerToCashResponseV1Schema,
  propertyAddressResolveV1Schema,
  propertyAddressResultV1Schema,
  propertyMapConfigurationV1Schema,
  routeInputsResolveV1Schema,
  routeInputsResultV1Schema,
  type FoundationConsoleSessionV1,
  type CustomerTypeChangeApplyRequestV1,
  type CustomerTypeChangeApplyResponseV1,
  type CustomerTypeChangePreviewRequestV1,
  type CustomerTypeChangePreviewResponseV1,
  type CustomerToCashReportRequestV1,
  type CustomerToCashReportV1,
  type CustomerStatementRequestV1,
  type CustomerStatementV1,
  type DispatchBoardActionRequestV1,
  type DispatchBoardActionResponseV1,
  type DispatchBoardReadV1,
  type DispatchDayOfChangeRequestV1,
  type DispatchDayOfChangeResponseV1,
  type DispatchOptimizationJobV1,
  type DispatchOptimizationStartRequestV1,
  type DispatchOptimizationStartResponseV1,
  type SupportCaseBoardV1,
  type OperationsDashboardV1,
  type FoundationMutationRequestV1,
  type FoundationMutationResponseV1,
  type FinanceConsoleCommandDraftV1,
  type FinanceReferenceDataRequestV1,
  type FinanceReferenceDataV1,
  type FinancialReportRequestV1,
  type FinancialReportV1,
  type CommandResultV1,
  type FoundationProjectionReadV1,
  type InternalCustomerCreateRequestV1,
  type InternalCustomerCreateResponseV1,
  type InternalCustomerDuplicatePreviewRequestV1,
  type InternalCustomerDuplicatePreviewResponseV1,
  type SandboxCustomerToCashRequestV1,
  type SandboxCustomerToCashResponseV1,
  type PropertyAddressResolveV1,
  type PropertyAddressResultV1,
  type PropertyMapConfigurationV1,
  type RouteInputsResolveV1,
  type RouteInputsResultV1,
  type MembershipId,
  type MembershipPersonaV1,
  type TenantId,
} from "@impetusos/contracts"

import { propertyMapImageUrl as buildPropertyMapImageUrl } from "../domain/property-mapping.js"

export class ConsoleRequestError extends Error {
  override readonly name = "ConsoleRequestError"
  constructor(readonly kind: "denied" | "retry" | "invalid") {
    super("Foundation workspace request failed.")
  }
}

const request = async (path: string, init?: RequestInit): Promise<Response> => {
  try {
    const headers = new Headers(init?.headers)
    if (init?.body !== undefined) headers.set("Content-Type", "application/json")
    return await fetch(path, {
      ...init,
      credentials: "same-origin",
      headers,
    })
  } catch {
    throw new ConsoleRequestError("retry")
  }
}

const parse = async <T>(
  response: Response,
  schema: { parse(value: unknown): T }
): Promise<T> => {
  if (!response.ok) {
    throw new ConsoleRequestError(
      response.status === 401 || response.status === 403 ? "denied" : "retry"
    )
  }
  try {
    return schema.parse(await response.json())
  } catch {
    throw new ConsoleRequestError("invalid")
  }
}

export const readSession = async (): Promise<FoundationConsoleSessionV1 | null> => {
  const response = await request("/api/session")
  if (response.status === 401) return null
  return parse(response, foundationConsoleSessionV1Schema)
}

export const signIn = async (
  persona: MembershipPersonaV1
): Promise<FoundationConsoleSessionV1> =>
  parse(await request("/api/session/sign-in", {
    method: "POST",
    body: JSON.stringify({ persona }),
  }), foundationConsoleSessionV1Schema)

export const signOut = async (): Promise<void> => {
  const response = await request("/api/session/sign-out", {
    method: "POST",
    body: "{}",
  })
  if (!response.ok) throw new ConsoleRequestError("retry")
}

export const switchTenant = async (
  membershipId: MembershipId,
  tenantId: TenantId
): Promise<FoundationConsoleSessionV1> =>
  parse(await request("/api/session/switch", {
    method: "POST",
    body: JSON.stringify({ membershipId, tenantId }),
  }), foundationConsoleSessionV1Schema)

export const readFoundation = async (): Promise<FoundationProjectionReadV1> =>
  parse(await request("/api/foundation"), foundationProjectionReadV1Schema)

export const readDispatchBoard = async (): Promise<DispatchBoardReadV1> =>
  parse(await request("/api/dispatch/board"), dispatchBoardReadV1Schema)

export const readSupportCaseBoard = async (): Promise<SupportCaseBoardV1> =>
  parse(await request("/api/support/cases"), supportCaseBoardV1Schema)

export const readOperationsDashboard = async (): Promise<OperationsDashboardV1> =>
  parse(
    await request("/api/operations/dashboard"),
    operationsDashboardV1Schema,
  )

export const dispatchBoardAction = async (
  value: DispatchBoardActionRequestV1
): Promise<DispatchBoardActionResponseV1> => {
  const response = await request("/api/dispatch/board/actions", {
    method: "POST",
    body: JSON.stringify(dispatchBoardActionRequestV1Schema.parse(value)),
  })
  if ([200, 403, 409, 503].includes(response.status)) {
    return dispatchBoardActionResponseV1Schema.parse(await response.json())
  }
  return parse(response, dispatchBoardActionResponseV1Schema)
}

export const dispatchDayOfChange = async (
  value: DispatchDayOfChangeRequestV1
): Promise<DispatchDayOfChangeResponseV1> => {
  const response = await request("/api/dispatch/board/day-of", {
    method: "POST",
    body: JSON.stringify(dispatchDayOfChangeRequestV1Schema.parse(value)),
  })
  if ([200, 403, 409, 503].includes(response.status)) {
    return dispatchDayOfChangeResponseV1Schema.parse(await response.json())
  }
  return parse(response, dispatchDayOfChangeResponseV1Schema)
}

export const startDispatchOptimization = async (
  value: DispatchOptimizationStartRequestV1
): Promise<DispatchOptimizationStartResponseV1> =>
  parse(await request("/api/dispatch/board/optimize", {
    method: "POST",
    body: JSON.stringify(dispatchOptimizationStartRequestV1Schema.parse(value)),
  }), dispatchOptimizationStartResponseV1Schema)

export const readDispatchOptimizationJob = async (
  jobId: string
): Promise<DispatchOptimizationJobV1> =>
  parse(
    await request(`/api/dispatch/board/optimization/${encodeURIComponent(jobId)}`),
    dispatchOptimizationJobV1Schema
  )

export const readPropertyMapConfiguration =
  async (): Promise<PropertyMapConfigurationV1> =>
    parse(
      await request("/api/property-mapping/config"),
      propertyMapConfigurationV1Schema,
    )

export const resolvePropertyAddress = async (
  value: PropertyAddressResolveV1,
): Promise<PropertyAddressResultV1> =>
  parse(
    await request("/api/property-mapping/geocode", {
      method: "POST",
      body: JSON.stringify(propertyAddressResolveV1Schema.parse(value)),
    }),
    propertyAddressResultV1Schema,
  )

export const resolveRouteInputs = async (
  value: RouteInputsResolveV1,
): Promise<RouteInputsResultV1> =>
  parse(
    await request("/api/property-mapping/route-inputs", {
      method: "POST",
      body: JSON.stringify(routeInputsResolveV1Schema.parse(value)),
    }),
    routeInputsResultV1Schema,
  )

export const propertyMapImageUrl = buildPropertyMapImageUrl

export const readCustomerToCashReport = async (
  value: CustomerToCashReportRequestV1
): Promise<CustomerToCashReportV1> => {
  const query = new URLSearchParams({
    queue: value.queue,
    cursor: String(value.cursor),
    pageSize: String(value.pageSize),
  })
  if (value.legalEntityId !== null) query.set("legalEntityId", value.legalEntityId)
  if (value.branchId !== null) query.set("branchId", value.branchId)
  return parse(
    await request(`/api/reports/customer-to-cash?${query.toString()}`),
    customerToCashReportV1Schema
  )
}

export const readFinancialReport = async (
  value: FinancialReportRequestV1,
): Promise<FinancialReportV1> => {
  const query = new URLSearchParams({
    kind: value.kind,
    legalEntityId: value.legalEntityId,
    fromDate: value.fromDate,
    toDate: value.toDate,
    cursor: String(value.cursor),
    pageSize: String(value.pageSize),
  })
  if (value.branchId !== null) query.set("branchId", value.branchId)
  return parse(
    await request(`/api/finance/reports?${query.toString()}`),
    financialReportV1Schema,
  )
}

export const readFinanceReferenceData = async (
  value: FinanceReferenceDataRequestV1,
): Promise<FinanceReferenceDataV1> => {
  const parsed = financeReferenceDataRequestV1Schema.parse(value)
  const query = new URLSearchParams({
    legalEntityId: parsed.legalEntityId,
    branchId: parsed.branchId,
  })
  return parse(
    await request(`/api/finance/reference-data?${query.toString()}`),
    financeReferenceDataV1Schema,
  )
}

export const dispatchFinanceCommand = async (
  value: FinanceConsoleCommandDraftV1,
): Promise<CommandResultV1> => {
  const response = await request("/api/finance/commands", {
    method: "POST",
    body: JSON.stringify(financeConsoleCommandDraftV1Schema.parse(value)),
  })
  if ([200, 409, 503].includes(response.status)) {
    return commandResultV1Schema.parse(await response.json())
  }
  return parse(response, commandResultV1Schema)
}

export const readCustomerStatement = async (
  value: CustomerStatementRequestV1
): Promise<CustomerStatementV1> => {
  const query = new URLSearchParams({
    customerId: value.customerId,
    fromDate: value.fromDate,
    toDate: value.toDate,
    cursor: String(value.cursor),
    pageSize: String(value.pageSize),
  })
  return parse(
    await request(`/api/customer-statement?${query.toString()}`),
    customerStatementV1Schema,
  )
}

export const runSandboxCustomerToCash = async (
  value: SandboxCustomerToCashRequestV1
): Promise<SandboxCustomerToCashResponseV1> => {
  const response = await request("/api/sandbox/customer-to-cash", {
    method: "POST",
    body: JSON.stringify(sandboxCustomerToCashRequestV1Schema.parse(value)),
  })
  if ([200, 403, 409, 503].includes(response.status)) {
    return sandboxCustomerToCashResponseV1Schema.parse(await response.json())
  }
  return parse(response, sandboxCustomerToCashResponseV1Schema)
}

export const mutateFoundation = async (
  mutation: FoundationMutationRequestV1
): Promise<FoundationMutationResponseV1> =>
  parse(await request("/api/foundation/mutate", {
    method: "POST",
    body: JSON.stringify(mutation),
  }), foundationMutationResponseV1Schema)

export const previewInternalCustomerDuplicates = async (
  value: InternalCustomerDuplicatePreviewRequestV1
): Promise<InternalCustomerDuplicatePreviewResponseV1> =>
  parse(await request("/api/customers/duplicate-preview", {
    method: "POST",
    body: JSON.stringify(value),
  }), internalCustomerDuplicatePreviewResponseV1Schema)

export const createInternalCustomer = async (
  value: InternalCustomerCreateRequestV1
): Promise<InternalCustomerCreateResponseV1> =>
  parse(await request("/api/customers", {
    method: "POST",
    body: JSON.stringify(value),
  }), internalCustomerCreateResponseV1Schema)

export const previewCustomerTypeChange = async (
  value: CustomerTypeChangePreviewRequestV1
): Promise<CustomerTypeChangePreviewResponseV1> =>
  parse(await request("/api/customers/type-change-preview", {
    method: "POST",
    body: JSON.stringify(value),
  }), customerTypeChangePreviewResponseV1Schema)

export const applyCustomerTypeChange = async (
  value: CustomerTypeChangeApplyRequestV1
): Promise<CustomerTypeChangeApplyResponseV1> =>
  parse(await request("/api/customers/type-change", {
    method: "POST",
    body: JSON.stringify(value),
  }), customerTypeChangeApplyResponseV1Schema)
